import { getApiUrl } from '../config/apiConfig.ts';
import { TrendItem } from '../domain/types.ts';

export const FALLBACK_TRENDS: string[] = [
  'Đội tuyển Việt Nam thắng kịch tính phút bù giờ',
  'Giá vàng nhẫn lại lập đỉnh mới sáng nay',
  'Concert Anh Trai Say Hi cháy vé sau 5 phút',
  'Cơn sốt trà sữa đất nung lan rộng khắp các phố',
  'Hà Nội chuẩn bị đón đợt gió mùa đông bắc se lạnh',
  'iPhone thế hệ mới mở bán chính hãng tại Việt Nam',
  'Phim điện ảnh Việt cán mốc 400 tỷ doanh thu phòng vé',
  'Trào lưu check-in cà phê đường tàu hot rần rần',
  'Táo Quân rục rịch tập luyện cho đêm Giao thừa',
  'Đề thi tốt nghiệp có nhiều điểm đổi mới',
  'Sốt giải chạy Marathon đêm phố cổ',
  'Món bánh mì thanh long lại gây bão cộng đồng mạng',
  'Vé máy bay dịp lễ bất ngờ hạ nhiệt',
  'Hội Lim rộn ràng câu ca quan họ mùa lễ hội',
  'Mưa giông đầu mùa giúp hạ nhiệt sau chuỗi ngày oi bức',
];

export const FALLBACK_TREND_ITEMS: TrendItem[] = FALLBACK_TRENDS.map((t, idx) => ({
  query: t,
  headline: t,
  traffic: idx % 3 === 0 ? '10K+' : idx % 2 === 0 ? '5K+' : '2K+',
  source: idx % 3 === 0 ? 'Báo Lao Động' : idx % 2 === 0 ? 'Báo VietNamNet' : 'Báo Tuổi Trẻ',
  url: 'https://trends.google.com/trending/rss?geo=VN',
}));

const LOCAL_STORAGE_KEY = 'tradaviahe_trends_cache';
const CLIENT_CACHE_TTL_MS = 30 * 60 * 1000; // 30 phút

interface CachedTrendsData {
  timestamp: number;
  trends: string[];
  items?: TrendItem[];
  sourceUrl?: string;
}

export class TrendsService {
  private currentTopicIndex = 0;
  private cachedTopics: string[] = [...FALLBACK_TRENDS];
  private cachedItems: TrendItem[] = [...FALLBACK_TREND_ITEMS];
  private sourceUrl = 'https://trends.google.com/trending/rss?geo=VN';

  constructor() {
    this.restoreFromLocalStorage();
  }

  private restoreFromLocalStorage(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as CachedTrendsData;
      if (data && Array.isArray(data.trends) && data.trends.length > 0) {
        const isFresh = Date.now() - data.timestamp < CLIENT_CACHE_TTL_MS;
        if (isFresh) {
          this.cachedTopics = data.trends;
          if (Array.isArray(data.items) && data.items.length > 0) {
            this.cachedItems = data.items;
          }
          if (data.sourceUrl) {
            this.sourceUrl = data.sourceUrl;
          }
        }
      }
    } catch {
      // ignore storage error
    }
  }

  private saveToLocalStorage(trends: string[], items: TrendItem[]): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const payload: CachedTrendsData = {
        timestamp: Date.now(),
        trends,
        items,
        sourceUrl: this.sourceUrl,
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage error
    }
  }

  public async fetchLiveTrends(force = false): Promise<string[]> {
    // Nếu cache local còn hạn và không force, không cần gọi mạng
    try {
      if (!force && typeof window !== 'undefined' && window.localStorage) {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw) as CachedTrendsData;
          if (data && Array.isArray(data.trends) && data.trends.length > 0) {
            const isFresh = Date.now() - data.timestamp < CLIENT_CACHE_TTL_MS;
            if (isFresh) {
              this.cachedTopics = data.trends;
              if (Array.isArray(data.items) && data.items.length > 0) {
                this.cachedItems = data.items;
              }
              return this.cachedTopics;
            }
          }
        }
      }
    } catch {
      // ignore
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);

      const endpoint = force ? '/api/trends?force=1' : '/api/trends';
      const targetUrl = getApiUrl(endpoint);
      const res = await fetch(targetUrl, { signal: controller.signal });
      clearTimeout(timer);

      if (res.ok) {
        const data = (await res.json()) as {
          success?: boolean;
          trends?: unknown;
          items?: unknown;
          sourceUrl?: string;
        };

        if (data && data.success && Array.isArray(data.trends) && data.trends.length > 0) {
          const stringTrends = data.trends.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
          if (stringTrends.length > 0) {
            this.cachedTopics = stringTrends;
          }

          if (Array.isArray(data.items) && data.items.length > 0) {
            this.cachedItems = data.items as TrendItem[];
          }

          if (data.sourceUrl) {
            this.sourceUrl = data.sourceUrl;
          }

          this.saveToLocalStorage(this.cachedTopics, this.cachedItems);
        }
      }
    } catch {
      // Keep cached fallback topics on error/timeout
    }

    return this.cachedTopics;
  }

  public getCurrentTopic(): string {
    return this.cachedTopics[this.currentTopicIndex] ?? this.cachedTopics[0] ?? FALLBACK_TRENDS[0]!;
  }

  public rotateTopic(): string {
    if (this.cachedTopics.length === 0) {
      this.cachedTopics = [...FALLBACK_TRENDS];
    }
    this.currentTopicIndex = (this.currentTopicIndex + 1) % this.cachedTopics.length;
    return this.getCurrentTopic();
  }

  public getAllTopics(): string[] {
    return [...this.cachedTopics];
  }

  public getDetailedItems(): TrendItem[] {
    return [...this.cachedItems];
  }

  public getSourceUrl(): string {
    return this.sourceUrl;
  }
}

